import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { auction as auctionApi, funds as fundsApi, files as filesApi } from '../services/api';
import ClampWithMore from '../components/ClampWithMore';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { Gavel, Clock, Users, Loader2, Award, Flag, HeartHandshake, KeyRound, X, EyeOff, Lock, CreditCard, Truck } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import PayPalTrustBadge from '../components/PayPalTrustBadge';
import CategoryChips from '../components/CategoryChips';
import AppImage from '../components/AppImage';
import SiteSections from '../components/SiteSections';

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const FLAG_REASONS = [
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'misrepresented', label: 'Condition is misrepresented' },
  { value: 'fake_or_stolen', label: 'Suspected fake or stolen item' },
  { value: 'other', label: 'Other' },
];

function endsAtDisplay(expiresAt) {
  if (!expiresAt) return '';
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 'ending soon';
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function AuctionStore() {
  const { user, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [funds, setFunds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [flagFor, setFlagFor] = useState(null); // { id, title } | null
  const [flagReason, setFlagReason] = useState('inappropriate');
  const [flagDetail, setFlagDetail] = useState('');
  const [confirmFor, setConfirmFor] = useState(null); // { id, title } | null
  const [confirmCode, setConfirmCode] = useState('');
  const [silentFor, setSilentFor] = useState(null); // { id, title } | null
  const [silentAmount, setSilentAmount] = useState('');
  const [deliveryFor, setDeliveryFor] = useState(null); // full auction item
  const [cat, setCat] = useState('All');

  const load = async () => {
    try {
      const [list, fundsLive] = await Promise.all([auctionApi.list(), fundsApi.live()]);
      setItems(list);
      setFunds(fundsLive);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const placeBid = async (id) => {
    setBusyId(id);
    try {
      const updated = await auctionApi.bid(id);
      setItems((prev) => prev.map((it) => (it.id === id ? updated : it)));
      await refresh();
      toast({ title: 'Bid placed!', description: 'Your 10 pts are locked into the C.A.N. Capital Fund. Highest bidder at auction end wins.' });
    } catch (err) {
      toast({ title: 'Bid failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setBusyId(null); }
  };

  const submitFlag = async () => {
    if (!flagFor) return;
    setBusyId(flagFor.id);
    try {
      await auctionApi.flag(flagFor.id, flagReason, flagDetail);
      toast({ title: 'Reported', description: 'Thanks. Our team will review this listing.' });
      setFlagFor(null); setFlagReason('inappropriate'); setFlagDetail('');
      await load();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setBusyId(null); }
  };

  const submitConfirm = async () => {
    if (!confirmFor) return;
    setBusyId(confirmFor.id);
    try {
      await auctionApi.confirmCode(confirmFor.id, confirmCode.trim());
      toast({ title: 'Pickup confirmed!', description: '+100 pts credited. Thank you for donating.' });
      setConfirmFor(null); setConfirmCode('');
      await refresh(); await load();
    } catch (err) {
      toast({ title: 'Invalid code', description: err?.response?.data?.detail || 'Try again' });
    } finally { setBusyId(null); }
  };

  const submitSilent = async () => {
    if (!silentFor) return;
    const amt = parseInt(silentAmount, 10);
    if (!amt || amt <= 0) { toast({ title: 'Enter a positive amount' }); return; }
    setBusyId(silentFor.id);
    try {
      await auctionApi.silentBid(silentFor.id, amt);
      toast({ title: 'Silent bid submitted', description: 'Highest sealed bid at the deadline wins. Good luck.' });
      setSilentFor(null); setSilentAmount('');
      await refresh(); await load();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setBusyId(null); }
  };

  const catFilter = (i) => cat === 'All' || (i.donorCategory === cat);
  const active = items.filter((i) => i.status === 'active' && catFilter(i));
  const silent = items.filter((i) => i.status === 'silent_bid' && catFilter(i));
  const sold = items.filter((i) => ['sold', 'completed'].includes(i.status) && catFilter(i));
  const catCounts = useMemo(() => {
    const m = { All: items.length };
    items.forEach(i => { if (i.donorCategory) m[i.donorCategory] = (m[i.donorCategory] || 0) + 1; });
    return m;
  }, [items]);

  return (
    <div className="space-y-6">
      <div className="pbb-card p-5 bg-gradient-to-br from-red-500/10 to-violet-500/5 border-red-500/20">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <HeartHandshake className="text-green-400" size={20} />
              <h1 className="text-2xl font-bold">C.A.N. Auction Store</h1>
            </div>
            <p className="text-gray-300 text-sm mt-1 leading-relaxed">
              Every bid costs <span className="text-yellow-300 font-semibold">10 points</span>. The winning bidder pays the discounted cash price. You may win a bid and choose not to purchase up to two times within a 30-day cycle. On the third time, you <span className="text-red-300 font-semibold">forfeit all points earned through bidding</span>. From today through <span className="text-white font-semibold">March 31, 2027</span>, 100% of funds go to the <span className="text-green-400 font-semibold">Promote Black Business LLC Capital Fund</span> to operate soft-launch promotions and prepare for the April 1, 2027 launch. Beginning <span className="text-white font-semibold">April 1, 2027</span>, 100% of proceeds are split <span className="text-green-400 font-semibold">50/50</span> between the Community Fund and the Young Leadership Fund.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-gray-400">Raised via auction points</div>
            <div className="text-3xl font-bold text-green-400">{(funds?.totalPts || 0).toLocaleString()} pts</div>
            <div className="text-[11px] text-gray-500 max-w-[220px] ml-auto">
              {(funds?.allocationPhase === 'split')
                ? <>Community <span className="text-green-300">{(funds?.communityPts || 0).toLocaleString()}</span> · Young Leadership <span className="text-green-300">{(funds?.youthPts || 0).toLocaleString()}</span></>
                : <>All going to <span className="text-yellow-300">Capital Fund</span> until Mar 31, 2027 · Community + Young Leadership 50/50 from Apr 1, 2027</>}
            </div>
          </div>
        </div>
      </div>

      <div className="px-3 py-1.5 rounded-full bg-violet-500/15 border border-violet-500/30 text-violet-300 text-sm inline-flex items-center gap-2 w-max">
        <Award size={14} /> {user?.points?.toLocaleString() || 0} pts available
      </div>

      <CategoryChips value={cat} onChange={setCat} counts={catCounts} testId="auction-cats" />

      {loading ? (
        <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : active.length === 0 ? (
        <div className="pbb-card border-2 border-dashed border-green-500/30 bg-green-500/5 p-10 text-center" data-testid="auction-empty-state">
          <Gavel className="mx-auto text-green-400" size={30} />
          <h3 className="mt-4 text-xl font-bold">Be one of the first 100 bidders</h3>
          <p className="mt-2 text-sm text-gray-300 max-w-md mx-auto">Auctions launching soon. <span className="font-semibold text-green-400">Register now to claim your spot</span> — founding bidders get priority access when the C.A.N. Auction Store opens April 1.</p>
          <Link to="/signup" data-testid="auction-empty-cta" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-600 hover:bg-green-500 text-white text-sm font-semibold">Register Free</Link>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {active.map((it) => {
              const isDonor = it.donorId === user?.id;
              const isHighestBidder = it.highestBidderId === user?.id;
              const bidCost = it.bidCost || 10;
              const setPrice = it.setPrice || 0;
              const shipping = it.shippingCost || 0;
              const taxes = it.taxes || 0;
              const handling = it.handling || 0;
              const totalDue = setPrice + shipping + taxes + handling;
              return (
                <div key={it.id} data-testid={`auction-${it.id}`} className="pbb-card overflow-hidden border-red-600/20 hover:border-red-500/60 transition-colors flex flex-col h-full">
                  <AppImage src={filesApi.urlFor(it.image)} alt={it.title} rounded="none" fallback={<Gavel size={32}/>} />
                  <div className="p-4 flex flex-col flex-1">
                    <ClampWithMore text={it.title} lines={2} className="font-semibold text-sm" testId={`auction-title-${it.id}`} />
                    <ClampWithMore text={it.description} lines={3} className="text-xs text-gray-400 mt-2" testId={`auction-desc-${it.id}`} />
                    <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
                      <span className="inline-flex items-center gap-1"><Clock size={12} /> {endsAtDisplay(it.expiresAt || it.endsAt)}</span>
                      <span className="inline-flex items-center gap-1"><Users size={12} /> {it.bids || 0} bids</span>
                    </div>
                    <div className="mt-3 flex items-end justify-between">
                      <div>
                        <div className="text-xs text-gray-500">Winner pays</div>
                        <div className="text-xl font-bold text-emerald-300">${setPrice.toFixed(2)}</div>
                        {it.estimatedValue ? <div className="text-[10px] text-gray-500">Retail ${it.estimatedValue.toFixed(0)} · pot {(it.currentBid || 0).toLocaleString()} pts</div> : null}
                      </div>
                      {isDonor ? (
                        <div className="text-[11px] text-amber-400 text-right">Your item · can't bid</div>
                      ) : (
                        <button data-testid={`bid-btn-${it.id}`} onClick={() => placeBid(it.id)}
                          disabled={busyId === it.id || (user?.points || 0) < bidCost}
                          className="px-3 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-sm text-white disabled:opacity-50">
                          {busyId === it.id ? <Loader2 size={12} className="animate-spin inline" /> : `Bid ${bidCost} pts`}
                        </button>
                      )}
                    </div>
                    {(shipping + taxes + handling) > 0 && (
                      <div className="mt-2 text-[10px] text-gray-500">
                        + ${shipping.toFixed(2)} ship · ${taxes.toFixed(2)} tax · ${handling.toFixed(2)} handling = <span className="text-emerald-400 font-semibold">${totalDue.toFixed(2)} due</span>
                      </div>
                    )}
                    <div className="mt-2 flex items-center justify-between text-[11px]">
                      <div>
                        {(it.currentBid || 0) >= 400 && (
                          <div className="text-purple-300 inline-flex items-center gap-1 mr-2"><Lock size={10} /> Silent phase near ({500 - (it.currentBid || 0)} to trigger)</div>
                        )}
                        {isHighestBidder && <span className="text-green-400">You're the highest bidder</span>}
                        {!isDonor && !isHighestBidder && (
                          <span className="text-gray-500">Every bid costs 10 points. The winning bidder pays the discounted cash price — proceeds support the Promote Black Business LLC Capital Fund.</span>
                        )}
                      </div>
                      {!isDonor && (
                        <button data-testid={`flag-btn-${it.id}`} onClick={() => setFlagFor({ id: it.id, title: it.title })}
                          className="text-gray-500 hover:text-orange-400 inline-flex items-center gap-1">
                          <Flag size={11} /> Report
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {silent.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mt-6 mb-3">
                <EyeOff className="text-purple-400" size={16} />
                <h2 className="text-sm uppercase tracking-wider text-purple-300 font-semibold">Silent bid phase · sealed bids</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {silent.map((it) => {
                  const isParticipant = (it.silentParticipants || []).some((p) => p.userId === user?.id);
                  const isDonor = it.donorId === user?.id;
                  return (
                    <div key={it.id} data-testid={`silent-${it.id}`} className="pbb-card overflow-hidden border-purple-500/40 bg-gradient-to-br from-purple-500/10 to-transparent flex flex-col h-full">
                      <AppImage src={filesApi.urlFor(it.image)} alt={it.title} rounded="none" fallback={<Gavel size={32}/>} />
                      <div className="p-4 flex flex-col flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold text-sm min-w-0 pbb-card-title" title={it.title}>{it.title}</div>
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-purple-500/40 bg-purple-500/15 text-purple-300 inline-flex items-center gap-1">
                            <Lock size={9} /> Sealed
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
                          <span className="inline-flex items-center gap-1"><Clock size={12} /> {endsAtDisplay(it.silentEndsAt)} left</span>
                          <span className="inline-flex items-center gap-1"><Users size={12} /> {(it.silentParticipants || []).length}/10</span>
                        </div>
                        <p className="text-[11px] text-purple-300/80 mt-2 leading-relaxed">
                          Bidding heated up past {(500).toLocaleString()} pts. Top {(it.silentParticipants || []).length} bidders commit one sealed bid — highest wins, earliest submission breaks ties.
                        </p>
                        <div className="mt-3">
                          {isDonor ? (
                            <div className="text-[11px] text-amber-400">Your item — awaiting sealed bids</div>
                          ) : isParticipant ? (
                            <button data-testid={`silent-bid-${it.id}`} onClick={() => { setSilentFor({ id: it.id, title: it.title }); setSilentAmount(''); }}
                              disabled={busyId === it.id}
                              className="w-full px-3 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-sm text-white disabled:opacity-50 inline-flex items-center justify-center gap-1">
                              <EyeOff size={13} /> Commit sealed bid
                            </button>
                          ) : (
                            <div className="text-[11px] text-gray-500">You aren't in the top 10 bidders — sealed round is closed to you.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {sold.length > 0 && (
            <div>
              <h2 className="text-sm uppercase tracking-wider text-gray-500 font-semibold mt-6 mb-3">Recently sold / completed</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {sold.slice(0, 12).map((it) => {
                  const isDonor = it.donorId === user?.id;
                  const isWinner = it.winnerId === user?.id;
                  const needsCheckout = isWinner && it.checkoutStatus === 'awaiting_payment' && (it.checkoutTotal || 0) > 0;
                  const needsDelivery = isWinner && it.deliveryMethod === 'shipping' && (it.deliveryFee || 0) > 0 && !it.deliveryPaid && it.status === 'sold';
                  return (
                    <div key={it.id} className="pbb-card overflow-hidden opacity-90 flex flex-col h-full">
                      <AppImage src={filesApi.urlFor(it.image)} alt={it.title} rounded="none" fallback={<Gavel size={28}/>} />
                      <div className="p-4 flex flex-col flex-1">
                        <div className="font-semibold text-sm pbb-card-title" title={it.title}>{it.title}</div>
                        <div className="text-xs text-gray-400 mt-1">Sold for {(it.winningAmount || 0).toLocaleString()} pts</div>
                        {isDonor && it.status === 'sold' && (
                          <button data-testid={`enter-code-${it.id}`} onClick={() => setConfirmFor({ id: it.id, title: it.title })}
                            className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-500 text-black text-xs font-semibold">
                            <KeyRound size={12} /> Enter pickup code
                          </button>
                        )}
                        {isDonor && it.status === 'completed' && (
                          <div className="mt-2 text-[11px] text-green-400">Completed · +100 pts credited</div>
                        )}
                        {needsCheckout && (
                          <Link to={`/auction/${it.id}/checkout`} data-testid={`pay-checkout-${it.id}`}
                            className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-500 text-black text-xs font-semibold">
                            <CreditCard size={12} /> Complete checkout · ${(it.checkoutTotal || 0).toFixed(2)}
                          </Link>
                        )}
                        {isWinner && it.checkoutStatus === 'paid' && (
                          <div className="mt-2 text-[11px] text-green-400">Paid · donor will ship</div>
                        )}
                        {needsDelivery && !needsCheckout && (
                          <button data-testid={`pay-delivery-${it.id}`} onClick={() => setDeliveryFor(it)}
                            className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold">
                            <CreditCard size={12} /> Pay ${(it.deliveryFee || 0).toFixed(2)} delivery
                          </button>
                        )}
                        {isWinner && it.deliveryPaid && (
                          <div className="mt-2 text-[11px] text-blue-300">Delivery paid — donor will ship</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {active.length === 0 && (
            <div className="pbb-card p-10 text-center">
              <Gavel className="mx-auto text-red-500" size={28} />
              <div className="mt-3 font-semibold">No active auctions right now</div>
              <p className="text-sm text-gray-400 mt-1">Donate an item from the Donate page to be the first!</p>
            </div>
          )}
        </>
      )}

      {flagFor && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4">
          <div className="w-full max-w-md bg-[#0f0f0f] border border-[#1f1f1f] rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
              <div className="font-semibold">Report listing</div>
              <button onClick={() => setFlagFor(null)} className="text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-xs text-gray-400">Reporting <span className="text-white font-medium">{flagFor.title}</span></div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Reason</label>
                <select data-testid="flag-reason" value={flagReason} onChange={(e) => setFlagReason(e.target.value)}
                  className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm">
                  {FLAG_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Details (optional)</label>
                <textarea data-testid="flag-detail" value={flagDetail} onChange={(e) => setFlagDetail(e.target.value)} rows={3}
                  className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="text-[11px] text-gray-500">3 unique-user flags will automatically pull the listing for admin review.</div>
            </div>
            <div className="px-5 py-4 border-t border-[#1f1f1f] flex justify-end">
              <button data-testid="flag-submit" onClick={submitFlag} disabled={busyId === flagFor.id}
                className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 text-black text-sm font-semibold disabled:opacity-50">
                Submit report
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmFor && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4">
          <div className="w-full max-w-md bg-[#0f0f0f] border border-[#1f1f1f] rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
              <div className="font-semibold">Enter buyer's pickup code</div>
              <button onClick={() => setConfirmFor(null)} className="text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-xs text-gray-400">Completing pickup for <span className="text-white font-medium">{confirmFor.title}</span></div>
              <input data-testid="confirm-code-input" value={confirmCode} onChange={(e) => setConfirmCode(e.target.value.toUpperCase())}
                placeholder="6-character code" maxLength={6}
                className="w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-3 text-lg font-mono text-center tracking-widest" />
              <div className="text-[11px] text-gray-500">You'll earn 100 pts when the code matches. Ask the buyer to share the code shown in their auction win notification.</div>
            </div>
            <div className="px-5 py-4 border-t border-[#1f1f1f] flex justify-end">
              <button data-testid="confirm-code-submit" onClick={submitConfirm} disabled={busyId === confirmFor.id || confirmCode.length < 6}
                className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-black text-sm font-semibold disabled:opacity-50">
                Confirm pickup
              </button>
            </div>
          </div>
        </div>
      )}
      {silentFor && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4">
          <div className="w-full max-w-md bg-[#0f0f0f] border border-purple-500/30 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
              <div className="font-semibold inline-flex items-center gap-2"><Lock size={14} className="text-purple-400" /> Commit sealed bid</div>
              <button onClick={() => setSilentFor(null)} className="text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-xs text-gray-400">Sealed bid on <span className="text-white font-medium">{silentFor.title}</span></div>
              <div className="text-[11px] text-purple-300/80 leading-relaxed">
                Enter any amount up to your available points. Your bid stays private until the deadline. Highest amount wins — if there's a tie, the earliest submission wins. You can only submit <span className="font-semibold text-white">one</span> sealed bid, and this can't be undone.
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Your sealed amount (pts)</label>
                <input data-testid="silent-amount" type="number" min="1" value={silentAmount}
                  onChange={(e) => setSilentAmount(e.target.value)} placeholder="e.g. 1250"
                  className="mt-1 w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-3 text-lg font-mono text-center" />
                <div className="text-[11px] text-gray-500 mt-1">You have <span className="text-violet-300 font-semibold">{(user?.points || 0).toLocaleString()} pts</span> available.</div>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-[#1f1f1f] flex justify-end">
              <button data-testid="silent-submit" onClick={submitSilent}
                disabled={busyId === silentFor.id || !silentAmount || Number(silentAmount) <= 0 || Number(silentAmount) > (user?.points || 0)}
                className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-sm font-semibold disabled:opacity-50">
                {busyId === silentFor.id ? 'Submitting…' : 'Submit sealed bid'}
              </button>
            </div>
          </div>
        </div>
      )}
      {deliveryFor && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4 overflow-y-auto py-6">
          <div className="w-full max-w-md bg-[#0f0f0f] border border-blue-500/30 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
              <div className="font-semibold inline-flex items-center gap-2"><Truck size={16} className="text-blue-400" /> Pay delivery fee</div>
              <button onClick={() => setDeliveryFor(null)} className="text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-xs text-gray-400">You won <span className="text-white font-medium">{deliveryFor.title}</span>. The donor set a shipping fee of <span className="text-blue-300 font-semibold">${(deliveryFor.deliveryFee || 0).toFixed(2)}</span>. Pay this to the platform via PayPal and the donor will be notified to ship.</div>
              {deliveryFor.deliveryDescription && (
                <div className="rounded-lg border border-[#1f1f1f] bg-black px-3 py-2 text-xs text-gray-300">
                  <span className="text-gray-500">Donor's note:</span> {deliveryFor.deliveryDescription}
                </div>
              )}
              <div className="rounded-lg border border-[#1f1f1f] bg-black p-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-gray-400">Delivery fee</span><span className="font-mono">${(deliveryFor.deliveryFee || 0).toFixed(2)}</span></div>
                <div className="flex justify-between pt-1.5 border-t border-[#1f1f1f]"><span className="font-semibold">Total</span><span className="font-mono font-semibold text-blue-300">${(deliveryFor.deliveryFee || 0).toFixed(2)}</span></div>
              </div>
              {PAYPAL_CLIENT_ID ? (
                <div data-testid="delivery-paypal-wrapper">
                  <PayPalScriptProvider options={{ 'client-id': PAYPAL_CLIENT_ID, currency: 'USD', intent: 'capture' }}>
                    <PayPalButtons
                      style={{ layout: 'vertical', color: 'blue', shape: 'rect', label: 'paypal' }}
                      createOrder={async () => (await auctionApi.deliveryCreateOrder(deliveryFor.id)).orderId}
                      onApprove={async (data) => {
                        try {
                          await auctionApi.deliveryCapture(deliveryFor.id, data.orderID);
                          toast({ title: 'Delivery fee paid', description: 'The donor has been notified to ship your item.' });
                          setDeliveryFor(null);
                          await load();
                        } catch (err) { toast({ title: 'Capture failed', description: err?.response?.data?.detail || 'Try again' }); }
                      }}
                      onError={(err) => toast({ title: 'Payment error', description: String(err?.message || err) })}
                    />
                  </PayPalScriptProvider>
                  <div className="text-center mt-2"><PayPalTrustBadge compact /></div>
                </div>
              ) : (
                <div className="text-xs text-red-400">PayPal is not configured. Please contact support.</div>
              )}
            </div>
          </div>
        </div>
      )}
      <SiteSections pageKey="auction" />
    </div>
  );
}
